// src/Breakdown.js
import React, { useState, useEffect } from 'react';
import { Checkbox, FormControlLabel, Slider, Typography, Box } from '@mui/material';

const hotMarks = [
  { value: 20, label: '20' },
  { value: 30, label: '30' },
  { value: 40, label: '40' },
  { value: 45, label: '45' },
  { value: 50, label: '50' },
  { value: 60, label: '60' },
];

const coldMarks = [
  { value: 1, label: '1' },
  { value: 1.5, label: '1 1/2' },
  { value: 2, label: '2' },
  { value: 2.5, label: '2 1/2' },
  { value: 3, label: '3' },
];

function Breakdown({ 
  flights, 
  onParametersChange,
  isPriorityFlight: initialIsPriorityFlight = false,
  hotThreshold: initialHotThreshold = 30,
  coldThresholdHours: initialColdThresholdHours = 2,
  hasFreightManifest: initialHasFreightManifest = false
}) {
  const [isPriorityFlight, setIsPriorityFlight] = useState(initialIsPriorityFlight);
  const [hasFreightManifest, setHasFreightManifest] = useState(initialHasFreightManifest);
  const [hotThreshold, setHotThreshold] = useState(initialHotThreshold);
  const [coldThresholdHours, setColdThresholdHours] = useState(initialColdThresholdHours);

  // Initialize with props values
  useEffect(() => {
    setIsPriorityFlight(initialIsPriorityFlight);
    setHasFreightManifest(initialHasFreightManifest);
    setHotThreshold(initialHotThreshold);
    setColdThresholdHours(initialColdThresholdHours);
  }, [initialIsPriorityFlight, initialHasFreightManifest, initialHotThreshold, initialColdThresholdHours]);

  // Report parameter changes to parent
  useEffect(() => {
    if (onParametersChange) {
      onParametersChange({
        isPriorityFlight,
        hasFreightManifest,
        hotThreshold,
        coldThresholdHours
      });
    }
  }, [isPriorityFlight, hasFreightManifest, hotThreshold, coldThresholdHours, onParametersChange]);

  const handlePriorityChange = (e) => {
    const newValue = e.target.checked;
    setIsPriorityFlight(newValue);
    console.log("Priority Flight set to:", newValue, "type:", typeof newValue); // Debug log
  };

  const handleFreightManifestChange = (e) => {
    const newValue = e.target.checked;
    setHasFreightManifest(newValue);
    console.log("Freight Manifest set to:", newValue, "type:", typeof newValue); // Debug log
  };

  // Colors and styles based on priority status
  const hotLabelColor = isPriorityFlight ? '#888' : '#e2e2e2';
  const priorityIndicatorColor = '#F9B612'; // Gold color for priority

  return (
    <div style={{ color: '#e2e2e2' }}>

      {/* Checkboxes Row - Priority Flight and Freight Manifest */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: '1rem' }}>
        {/* Priority Flight Checkbox */}
        <Box 
          sx={{ 
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isPriorityFlight ? 'rgba(249, 182, 18, 0.1)' : 'transparent',
            border: isPriorityFlight ? '1px solid rgba(249, 182, 18, 0.3)' : 'none',
            flex: 1
          }}
        >
          <FormControlLabel
            sx={{ 
              color: isPriorityFlight ? priorityIndicatorColor : '#e2e2e2',
              fontWeight: isPriorityFlight ? 'bold' : 'normal',
            }}
            control={
              <Checkbox
                checked={isPriorityFlight}
                onChange={handlePriorityChange}
                sx={{
                  color: '#e2e2e2',
                  '&.Mui-checked': { color: priorityIndicatorColor },
                }}
              />
            }
            label={
              <Typography 
                sx={{ 
                  color: isPriorityFlight ? priorityIndicatorColor : '#e2e2e2',
                  fontWeight: isPriorityFlight ? 'bold' : 'normal'
                }}
              >
                Priority Flight?
              </Typography>
            }
          />
          {isPriorityFlight && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                marginLeft: '2.1rem', 
                color: priorityIndicatorColor,
                marginTop: '-0.5rem'
              }}
            >
              Connections ≤60 min will be highlighted in gold
            </Typography>
          )}
        </Box>

        {/* Freight Manifest Checkbox */}
        <Box 
          sx={{ 
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: hasFreightManifest ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
            border: hasFreightManifest ? '1px solid rgba(76, 175, 80, 0.3)' : 'none',
            flex: 1
          }}
        >
          <FormControlLabel
            sx={{ 
              color: hasFreightManifest ? '#4CAF50' : '#e2e2e2',
              fontWeight: hasFreightManifest ? 'bold' : 'normal',
            }}
            control={
              <Checkbox
                checked={hasFreightManifest}
                onChange={handleFreightManifestChange}
                sx={{
                  color: '#e2e2e2',
                  '&.Mui-checked': { color: '#4CAF50' },
                }}
              />
            }
            label={
              <Typography 
                sx={{ 
                  color: hasFreightManifest ? '#4CAF50' : '#e2e2e2',
                  fontWeight: hasFreightManifest ? 'bold' : 'normal'
                }}
              >
                Freight Manifest?
              </Typography>
            }
          />
          {hasFreightManifest && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                marginLeft: '2.1rem', 
                color: '#4CAF50',
                marginTop: '-0.5rem'
              }}
            >
              Include freight manifest data
            </Typography>
          )}
        </Box>
      </Box>

      {/* Hot Bag Threshold Slider */}
      <Typography style={{ marginTop: '1rem', color: hotLabelColor }}>
        Hot Bag Threshold: {hotThreshold} min
      </Typography>
      <Slider
        value={hotThreshold}
        onChange={(e, val) => setHotThreshold(val)}
        min={20}
        max={60}
        step={null}
        marks={hotMarks}
        valueLabelDisplay="auto"
        disabled={isPriorityFlight}
        sx={{
          width: '100%',
          '& .MuiSlider-track': { color: '#ff0000' },
          '& .MuiSlider-thumb': { color: '#ff0000' },
          '& .MuiSlider-rail': { color: '#ff0000', opacity: 0.3 },
          '& .MuiSlider-markLabel': { color: '#e2e2e2' },
          '& .MuiSlider-mark': { backgroundColor: '#fff', width: 2 },
          '& .MuiSlider-valueLabel': {
            backgroundColor: '#1f2937',
            color: '#e2e2e2',
          },
          '&.Mui-disabled': {
            pointerEvents: 'none',
            '& .MuiSlider-track': { color: '#888' },
            '& .MuiSlider-thumb': { color: '#888' },
            '& .MuiSlider-rail': { color: '#888', opacity: 0.3 },
            '& .MuiSlider-markLabel': { color: '#666' },
            '& .MuiSlider-mark': { backgroundColor: '#666' },
          },
        }}
      />
      {isPriorityFlight && (
        <Typography variant="caption" sx={{ display: 'block', color: '#888', marginTop: '0.2rem' }}>
          Hot bag threshold disabled in priority flight mode
        </Typography>
      )}

      {/* Cold Bag Threshold Slider */}
      <Typography style={{ marginTop: '1rem', color: '#e2e2e2' }}>
        Cold Bag Threshold: {coldThresholdHours} Hours
      </Typography>
      <Slider
        value={coldThresholdHours}
        onChange={(e, val) => setColdThresholdHours(val)}
        min={1}
        max={3}
        step={0.5}
        marks={coldMarks}
        valueLabelDisplay="auto"
        sx={{
          width: '100%',
          '& .MuiSlider-track': { color: '#1e4bc3' },
          '& .MuiSlider-thumb': { color: '#1e4bc3' },
          '& .MuiSlider-rail': { color: '#1e4bc3', opacity: 0.3 },
          '& .MuiSlider-markLabel': { color: '#e2e2e2' },
          '& .MuiSlider-mark': { backgroundColor: '#fff', width: 2 },
          '& .MuiSlider-valueLabel': {
            backgroundColor: '#1f2937',
            color: '#e2e2e2',
          },
        }}
      />

    </div>
  );
}

export default Breakdown;